import React, { useState, useEffect } from 'react';
import { MdPictureAsPdf, MdEmail, MdEdit, MdSave, MdAdd, MdDelete, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URL } from '../config/api';
import './WorkoutPlanViewer.css';

const ExerciseSearchInput = ({ value, onChange, exercises = [], exerciseGroups = [] }) => {
  const [search, setSearch] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filterGroup, setFilterGroup] = useState('');
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  useEffect(() => { setSearch(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = exercises.filter(ex => {
    const matchSearch = !search || ex.nome.toLowerCase().includes(search.toLowerCase());
    const matchGroup = !filterGroup || ex.gruppo_muscolare === filterGroup;
    return matchSearch && matchGroup;
  }).slice(0, 50);

  const handleSelect = (ex) => {
    setSearch(ex.nome);
    onChange(ex.nome);
    if (ex.machine_name) {
      setTimeout(() => {
        const event = new CustomEvent('exerciseSelected', { detail: { machineName: ex.machine_name } });
        inputRef.current?.dispatchEvent(event);
      }, 0);
    }
    setShowDropdown(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        className="wpv-input wpv-input-exercise-name"
        value={search}
        onChange={(e) => { setSearch(e.target.value); onChange(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        placeholder="Cerca esercizio..."
      />
      {showDropdown && exercises.length > 0 && (
        <div ref={dropdownRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#1e1e1e', border: '1px solid rgba(235, 205, 97, 0.3)', borderRadius: '8px',
          maxHeight: '280px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
        }}>
          {exerciseGroups.length > 0 && (
            <div style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
              <select
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                style={{ width: '100%', background: '#252525', color: '#fff', border: '1px solid rgba(235,205,97,0.2)', borderRadius: '4px', padding: '4px 8px', fontSize: '12px' }}
              >
                <option value="">Tutti i gruppi</option>
                {exerciseGroups.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          )}
          <div style={{ overflowY: 'auto', maxHeight: '230px' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', textAlign: 'center' }}>Nessun esercizio trovato</div>
            ) : (
              filtered.map(ex => (
                <div
                  key={ex.id}
                  onClick={() => handleSelect(ex)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(235, 205, 97, 0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ color: '#fff', fontWeight: '500' }}>{ex.nome}</span>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginLeft: '8px', whiteSpace: 'nowrap' }}>{ex.gruppo_muscolare}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const WorkoutPlanViewer = ({ plan, machines = [], exercises = [], exerciseGroups = [], onPlanChange, onSave, onShare, onDelete, readOnly = false, lastEmailSent = null, lastEmailStatus = null, startInEditMode = false, clientData = null }) => {
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [editedPlan, setEditedPlan] = useState(plan);
  const [hoveredInsertIndex, setHoveredInsertIndex] = useState({ dayIndex: null, exerciseIndex: null });

  useEffect(() => {
    setEditedPlan(plan);
  }, [plan]);

  useEffect(() => {
    setIsEditing(startInEditMode);
  }, [startInEditMode]);

  const getExerciseVideoUrl = (exerciseName) => {
    if (!exerciseName || !exercises || exercises.length === 0) return null;
    const match = exercises.find(ex => ex.nome === exerciseName);
    if (!match || !match.video_url) return null;
    const url = match.video_url;
    if (url.startsWith('http')) return url;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    if (normalized.startsWith('/uploads/')) return `${API_URL}/api${normalized}`;
    return `${API_URL}${normalized}`;
  };

  const handleSaveClick = () => {
    if (isEditing && onSave) {
      onSave(editedPlan);
    }
    setIsEditing(!isEditing);
  };

  const handleFieldChange = (path, value) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan)); // Deep copy
    const keys = path.split('.');
    let current = newPlan;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (key.includes('[')) {
        const [arrayKey, index] = key.split(/[[\]]/).filter(Boolean);
        if (!current[arrayKey]) current[arrayKey] = [];
        if (!current[arrayKey][parseInt(index)]) current[arrayKey][parseInt(index)] = {};
        current = current[arrayKey][parseInt(index)];
      } else {
        if (!current[key]) current[key] = {};
        current = current[key];
      }
    }
    
    const lastKey = keys[keys.length - 1];
    if (lastKey.includes('[')) {
      const [arrayKey, index] = lastKey.split(/[[\]]/).filter(Boolean);
      if (!current[arrayKey]) current[arrayKey] = [];
      current[arrayKey][parseInt(index)] = value;
    } else {
      current[lastKey] = value;
    }
    
    setEditedPlan(newPlan);
    if (onPlanChange) {
      onPlanChange(newPlan);
    }
  };

  const handleFrequencyChange = (newFreq) => {
    const freq = parseInt(newFreq) || 0;
    if (freq < 1 || freq > 7) return;
    
    const newPlan = JSON.parse(JSON.stringify(editedPlan));
    newPlan.frequenza_settimanale = freq;
    if (!newPlan.giorni) newPlan.giorni = [];

    const currentDays = newPlan.giorni.length;

    if (freq > currentDays) {
      for (let i = currentDays; i < freq; i++) {
        newPlan.giorni.push({
          giorno_numero: i + 1,
          nome_giorno: `Giorno ${i + 1}`,
          esercizi: []
        });
      }
    } else if (freq < currentDays) {
      newPlan.giorni = newPlan.giorni.slice(0, freq);
    }

    // Rinumera i giorni
    newPlan.giorni.forEach((g, i) => { g.giorno_numero = i + 1; });

    setEditedPlan(newPlan);
    if (onPlanChange) onPlanChange(newPlan);
  };

  const handleExerciseFieldChange = (dayIndex, exerciseIndex, field, value) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan)); // Deep copy
    if (newPlan.giorni && newPlan.giorni[dayIndex] && newPlan.giorni[dayIndex].esercizi && newPlan.giorni[dayIndex].esercizi[exerciseIndex]) {
      newPlan.giorni[dayIndex].esercizi[exerciseIndex][field] = value;
      setEditedPlan(newPlan);
      if (onPlanChange) {
        onPlanChange(newPlan);
      }
    }
  };

  const handleAddExercise = (dayIndex, insertIndex = null) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan)); // Deep copy
    if (!newPlan.giorni) newPlan.giorni = [];
    if (!newPlan.giorni[dayIndex]) newPlan.giorni[dayIndex] = { esercizi: [] };
    if (!newPlan.giorni[dayIndex].esercizi) newPlan.giorni[dayIndex].esercizi = [];
    
    const newExercise = {
      nome: 'Nuovo Esercizio',
      macchinario_target: '',
      serie: 3,
      ripetizioni: '10-12',
      recupero: '60s',
      note: '',
      tecnica_intensita: ''
    };
    
    // Se insertIndex è specificato, inserisci in quella posizione, altrimenti aggiungi alla fine
    if (insertIndex !== null && insertIndex >= 0) {
      newPlan.giorni[dayIndex].esercizi.splice(insertIndex, 0, newExercise);
    } else {
      newPlan.giorni[dayIndex].esercizi.push(newExercise);
    }
    
    setEditedPlan(newPlan);
    if (onPlanChange) {
      onPlanChange(newPlan);
    }
    // Reset hover state dopo l'inserimento
    setHoveredInsertIndex({ dayIndex: null, exerciseIndex: null });
  };

  const handleRemoveExercise = (dayIndex, exerciseIndex) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan)); // Deep copy
    if (newPlan.giorni && newPlan.giorni[dayIndex] && newPlan.giorni[dayIndex].esercizi) {
      newPlan.giorni[dayIndex].esercizi.splice(exerciseIndex, 1);
      setEditedPlan(newPlan);
      if (onPlanChange) {
        onPlanChange(newPlan);
      }
    }
  };

  const handleMoveExercise = (dayIndex, exerciseIndex, direction) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan)); // Deep copy
    if (!newPlan.giorni || !newPlan.giorni[dayIndex] || !newPlan.giorni[dayIndex].esercizi) return;

    const exercises = newPlan.giorni[dayIndex].esercizi;
    const newIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;

    if (newIndex < 0 || newIndex >= exercises.length) return;

    const temp = exercises[newIndex];
    exercises[newIndex] = exercises[exerciseIndex];
    exercises[exerciseIndex] = temp;

    // opzionale: aggiorna i numeri dei giorni o altri metadati se servisse
    setEditedPlan(newPlan);
    if (onPlanChange) {
      onPlanChange(newPlan);
    }
  };

  const handleAddBonusExercise = (dayIndex) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan));
    if (!newPlan.giorni || !newPlan.giorni[dayIndex]) return;
    if (!newPlan.giorni[dayIndex].esercizi_bonus) newPlan.giorni[dayIndex].esercizi_bonus = [];

    newPlan.giorni[dayIndex].esercizi_bonus.push({
      nome: 'Esercizio Bonus',
      macchinario_target: '',
      serie: 3,
      ripetizioni: '10-12',
      recupero: '60s',
      note: '',
      tecnica_intensita: ''
    });

    setEditedPlan(newPlan);
    if (onPlanChange) onPlanChange(newPlan);
  };

  const handleBonusFieldChange = (dayIndex, exerciseIndex, field, value) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan));
    if (newPlan.giorni?.[dayIndex]?.esercizi_bonus?.[exerciseIndex]) {
      newPlan.giorni[dayIndex].esercizi_bonus[exerciseIndex][field] = value;
      setEditedPlan(newPlan);
      if (onPlanChange) onPlanChange(newPlan);
    }
  };

  const handleRemoveBonusExercise = (dayIndex, exerciseIndex) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan));
    if (newPlan.giorni?.[dayIndex]?.esercizi_bonus) {
      newPlan.giorni[dayIndex].esercizi_bonus.splice(exerciseIndex, 1);
      setEditedPlan(newPlan);
      if (onPlanChange) onPlanChange(newPlan);
    }
  };

  const handleMoveBonusExercise = (dayIndex, exerciseIndex, direction) => {
    const newPlan = JSON.parse(JSON.stringify(editedPlan));
    if (!newPlan.giorni?.[dayIndex]?.esercizi_bonus) return;
    const arr = newPlan.giorni[dayIndex].esercizi_bonus;
    const newIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;
    if (newIndex < 0 || newIndex >= arr.length) return;
    [arr[newIndex], arr[exerciseIndex]] = [arr[exerciseIndex], arr[newIndex]];
    setEditedPlan(newPlan);
    if (onPlanChange) onPlanChange(newPlan);
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.8)); // Compressione 0.8
      };
      img.onerror = (e) => {
          console.warn("Errore caricamento immagine:", url);
          // Risolvi con null invece di reject per non bloccare tutto
          resolve(null);
      };
    });
  };

  const handleExportPDF = async () => {
    const doc = new jsPDF();
    
    // Colori Kinesis (approssimati)
    const goldColor = [235, 205, 97];
    const darkColor = [20, 20, 20];

    // Caricamento immagini macchinari
    const imagesData = {};
    if (machines && machines.length > 0 && plan.giorni) {
        const usedMachines = new Set();
        plan.giorni.forEach(g => g.esercizi?.forEach(e => {
            if (e.macchinario_target) usedMachines.add(e.macchinario_target);
        }));

        for (const name of usedMachines) {
            const machine = machines.find(m => m.nome === name);
            if (machine && machine.image_url) {
                const fullUrl = machine.image_url.startsWith('http') 
                    ? machine.image_url 
                    : `${API_URL}${machine.image_url}`;
                const base64 = await loadImage(fullUrl);
                if (base64) imagesData[name] = base64;
            }
        }
    }
    
    // Titolo
    doc.setFontSize(22);
    doc.setTextColor(...darkColor);
    doc.text(plan.nome_scheda || 'Scheda di Allenamento', 14, 22);
    
    // Info Generali
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`Durata: ${plan.durata_settimane || '-'} settimane | Frequenza: ${plan.frequenza_settimanale || '-'}x week`, 14, 32);
    
    if (plan.descrizione) {
        doc.setFontSize(10);
        const splitDesc = doc.splitTextToSize(plan.descrizione, 180);
        doc.text(splitDesc, 14, 40);
    }
    
    let yPos = plan.descrizione ? 55 : 45;

    // Dati Misurazione Cliente
    if (clientData) {
      if (clientData.bodyComposition) {
        const bc = clientData.bodyComposition;
        if (yPos > 230) { doc.addPage(); yPos = 20; }

        doc.setFontSize(14);
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        doc.text('Composizione Corporea', 14, yPos);
        yPos += 8;

        const bcHead = ['% Grasso', 'Massa Grassa', 'Massa Magra'];
        const bcRow = [`${bc.percentuale_grasso}%`, `${bc.massa_grassa_kg} kg`, `${bc.massa_magra_kg} kg`];
        if (clientData.peso) { bcHead.push('Peso'); bcRow.push(`${clientData.peso} kg`); }

        autoTable(doc, {
          startY: yPos,
          head: [bcHead],
          body: [bcRow],
          theme: 'grid',
          headStyles: { fillColor: goldColor, textColor: darkColor, fontStyle: 'bold' },
          styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
          margin: { left: 14, right: 14 },
        });
        yPos = doc.lastAutoTable.finalY + 6;

        if (clientData.measurementDate) {
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.setFont(undefined, 'normal');
          doc.text(`Rilevazione del ${new Date(clientData.measurementDate).toLocaleDateString('it-IT')}`, 14, yPos);
          yPos += 8;
        }
      }

      if (clientData.pliche) {
        const plicheEntries = [
          { key: 'pettorale', label: 'Pettorale' },
          { key: 'tricipite', label: 'Tricipite' },
          { key: 'ascellare', label: 'Ascellare' },
          { key: 'scapolare', label: 'Sottoscapolare' },
          { key: 'soprailiaca', label: 'Soprailiaca' },
          { key: 'addominale', label: 'Addominale' },
          { key: 'coscia', label: 'Coscia' },
          { key: 'bicipite', label: 'Bicipite' },
        ].filter(item => clientData.pliche[item.key] !== null);

        if (plicheEntries.length > 0) {
          if (yPos > 230) { doc.addPage(); yPos = 20; }

          doc.setFontSize(14);
          doc.setTextColor(...darkColor);
          doc.setFont(undefined, 'bold');
          doc.text('Plicometria', 14, yPos);
          yPos += 8;

          autoTable(doc, {
            startY: yPos,
            head: [plicheEntries.map(e => e.label)],
            body: [plicheEntries.map(e => `${clientData.pliche[e.key]} mm`)],
            theme: 'grid',
            headStyles: { fillColor: goldColor, textColor: darkColor, fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
            margin: { left: 14, right: 14 },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }
      }

      if (clientData.circonferenze) {
        const circEntries = [
          { key: 'spalle', label: 'Spalle' },
          { key: 'petto', label: 'Petto' },
          { key: 'braccio', label: 'Braccio' },
          { key: 'vita', label: 'Vita' },
          { key: 'fianchi', label: 'Fianchi' },
          { key: 'coscia', label: 'Coscia' },
          { key: 'polpaccio', label: 'Polpaccio' },
        ].filter(item => clientData.circonferenze[item.key] !== null);

        if (circEntries.length > 0) {
          if (yPos > 230) { doc.addPage(); yPos = 20; }

          doc.setFontSize(14);
          doc.setTextColor(...darkColor);
          doc.setFont(undefined, 'bold');
          doc.text('Circonferenze', 14, yPos);
          yPos += 8;

          autoTable(doc, {
            startY: yPos,
            head: [circEntries.map(e => e.label)],
            body: [circEntries.map(e => `${clientData.circonferenze[e.key]} cm`)],
            theme: 'grid',
            headStyles: { fillColor: goldColor, textColor: darkColor, fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
            margin: { left: 14, right: 14 },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }
      }
    }

    // Giorni
    if (plan.giorni && plan.giorni.length > 0) {
      plan.giorni.forEach((giorno, i) => {
        // Controllo pagina
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        doc.text(giorno.nome_giorno || `Giorno ${i+1}`, 14, yPos);
        yPos += 8;
        
        const tableBody = giorno.esercizi.map(ex => [
          '', // Placeholder per foto
          ex.nome + (ex.macchinario_target ? `\n(${ex.macchinario_target})` : ''),
          ex.serie || '-',
          ex.ripetizioni || '-',
          ex.recupero || '-',
          ex.tecnica_intensita || '-',
          ex.note || ''
        ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Foto', 'Esercizio', 'Serie', 'Reps', 'Rec.', 'Tecnica', 'Note']],
          body: tableBody,
          theme: 'grid',
          headStyles: { 
              fillColor: goldColor, 
              textColor: darkColor,
              fontStyle: 'bold'
          },
          styles: { fontSize: 9, cellPadding: 2, valign: 'middle' },
          columnStyles: {
              0: { cellWidth: 20, minCellHeight: 20 }, // Foto
              1: { cellWidth: 40 }, // Nome
              6: { cellWidth: 'auto' } // Note
          },
          didDrawCell: (data) => {
              if (data.section === 'body' && data.column.index === 0) {
                  const exIndex = data.row.index;
                  const esercizio = giorno.esercizi[exIndex];
                  if (esercizio && esercizio.macchinario_target && imagesData[esercizio.macchinario_target]) {
                      const imgData = imagesData[esercizio.macchinario_target];
                      // Calcola dimensioni per fit
                      const dim = 16;
                      const x = data.cell.x + 2;
                      const y = data.cell.y + 2;
                      try {
                        doc.addImage(imgData, 'JPEG', x, y, dim, dim);
                      } catch (e) {
                          console.warn('Errore rendering immagine PDF', e);
                      }
                  }
              }
          }
        });
        
        yPos = doc.lastAutoTable.finalY + 10;

        if (giorno.esercizi_bonus && giorno.esercizi_bonus.length > 0) {
          if (yPos > 250) { doc.addPage(); yPos = 20; }
          doc.setFontSize(11);
          doc.setTextColor(232, 168, 56);
          doc.setFont(undefined, 'bold');
          doc.text('⭐ Esercizi Bonus', 14, yPos);
          yPos += 6;

          const bonusBody = giorno.esercizi_bonus.map(ex => [
            '',
            ex.nome + (ex.macchinario_target ? `\n(${ex.macchinario_target})` : ''),
            ex.serie || '-',
            ex.ripetizioni || '-',
            ex.recupero || '-',
            ex.tecnica_intensita || '-',
            ex.note || ''
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Foto', 'Esercizio', 'Serie', 'Reps', 'Rec.', 'Tecnica', 'Note']],
            body: bonusBody,
            theme: 'grid',
            headStyles: { fillColor: [232, 168, 56], textColor: darkColor, fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 2, valign: 'middle' },
            columnStyles: { 0: { cellWidth: 20, minCellHeight: 20 }, 1: { cellWidth: 40 }, 6: { cellWidth: 'auto' } },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }

        yPos += 5;
      });
    }
    
    // Footer Consigli
    if (plan.consigli_trainer && plan.consigli_trainer.length > 0) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        
        doc.setFontSize(14);
        doc.text("Consigli del Trainer", 14, yPos);
        yPos += 8;
        
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        doc.setFont(undefined, 'normal');
        
        plan.consigli_trainer.forEach(tip => {
            const splitTip = doc.splitTextToSize(`• ${tip}`, 180);
            doc.text(splitTip, 14, yPos);
            yPos += (splitTip.length * 5) + 2;
        });
    }
    
    doc.save(`${(plan.nome_scheda || 'scheda').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
  };

  const handleSendEmail = () => {
    if (onShare) {
      onShare();
    } else {
      alert("Funzionalità non configurata");
    }
  };

  if (!plan) return null;

  const displayPlan = isEditing ? editedPlan : plan;

  return (
    <div className="workout-plan-viewer">
      <div className="wpv-header">
        <div className="wpv-header-content">
          <h2 className="wpv-title">{displayPlan.nome_scheda || 'Scheda di Allenamento'}</h2>
          <div className="wpv-actions">
            {lastEmailSent && (
              <div className="wpv-email-status" style={{ 
                fontSize: '12px', 
                color: 'rgba(255, 255, 255, 0.6)', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'flex-end',
                justifyContent: 'center',
                marginRight: '10px',
                textAlign: 'right'
              }}>
                <span>Ultimo invio: {new Date(lastEmailSent).toLocaleDateString()} {new Date(lastEmailSent).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                <span style={{ 
                  color: lastEmailStatus === 'Inviato' ? '#4caf50' : '#f44336',
                  fontWeight: '600'
                }}>
                  {lastEmailStatus}
                </span>
              </div>
            )}
            {!readOnly && (
            <button 
              className="wpv-btn wpv-btn-edit" 
              onClick={handleSaveClick}
              title={isEditing ? 'Salva modifiche' : 'Modifica scheda'}
            >
              {isEditing ? <MdSave /> : <MdEdit />}
              {isEditing ? ' Salva' : ' Modifica'}
            </button>
            )}
            <button 
              className="wpv-btn wpv-btn-pdf" 
              onClick={handleExportPDF}
              title="Esporta in PDF"
            >
              <MdPictureAsPdf />
              Esporta PDF
            </button>
            {!readOnly && (
            <button 
              className="wpv-btn wpv-btn-email" 
              onClick={handleSendEmail}
              title="Invia via Email"
            >
              <MdEmail />
              Invia Email
            </button>
            )}
            {!readOnly && onDelete && (
            <button 
              className="wpv-btn" 
              onClick={onDelete}
              title="Elimina Scheda"
              style={{ 
                  backgroundColor: 'rgba(244, 67, 54, 0.1)', 
                  color: '#f44336', 
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  marginLeft: 'auto'
              }}
            >
              <MdDelete />
              Elimina
            </button>
            )}
          </div>
        </div>
      </div>

      <div className="wpv-content">
        {/* Informazioni generali */}
        <div className="wpv-section">
          <h3 className="wpv-section-title">Informazioni Generali</h3>
          <div className="wpv-info-grid">
            <div className="wpv-info-item">
              <label className="wpv-label">Nome Scheda</label>
              {isEditing ? (
                <input
                  type="text"
                  className="wpv-input"
                  value={editedPlan.nome_scheda || ''}
                  onChange={(e) => handleFieldChange('nome_scheda', e.target.value)}
                />
              ) : (
                <div className="wpv-value">{displayPlan.nome_scheda || 'N/A'}</div>
              )}
            </div>
            <div className="wpv-info-item">
              <label className="wpv-label">Durata</label>
              {isEditing ? (
                <input
                  type="number"
                  className="wpv-input"
                  value={editedPlan.durata_settimane || ''}
                  onChange={(e) => handleFieldChange('durata_settimane', parseInt(e.target.value))}
                />
              ) : (
                <div className="wpv-value">{displayPlan.durata_settimane || 'N/A'} settimane</div>
              )}
            </div>
            <div className="wpv-info-item">
              <label className="wpv-label">Frequenza (giorni/sett.)</label>
              {isEditing ? (
                <input
                  type="number"
                  className="wpv-input"
                  min="1"
                  max="7"
                  value={editedPlan.frequenza_settimanale || ''}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                />
              ) : (
                <div className="wpv-value">{displayPlan.frequenza_settimanale || 'N/A'} giorni/settimana</div>
              )}
            </div>
            <div className="wpv-info-item">
              <label className="wpv-label">Split Routine</label>
              {isEditing ? (
                <select
                  className="wpv-input"
                  value={editedPlan.split_routine || ''}
                  onChange={(e) => handleFieldChange('split_routine', e.target.value)}
                  style={{ background: '#1e1e1e', color: '#fff', border: '1px solid #333', padding: '8px', borderRadius: '8px' }}
                >
                  <option value="">-- Seleziona --</option>
                  <option value="Full Body">Full Body</option>
                  <option value="Upper/Lower">Upper / Lower</option>
                  <option value="Push/Pull/Legs">Push / Pull / Legs</option>
                  <option value="Bro Split">Bro Split</option>
                  <option value="A/B Split">A/B Split</option>
                  <option value="Push/Pull">Push / Pull</option>
                  <option value="Cardio + Forza">Cardio + Forza</option>
                  <option value="Personalizzata">Personalizzata</option>
                </select>
              ) : (
                <div className="wpv-value">{displayPlan.split_routine || 'N/A'}</div>
              )}
            </div>
          </div>
        </div>

        {/* Dati Misurazione Cliente */}
        {!isEditing && clientData && (clientData.bodyComposition || clientData.pliche || clientData.circonferenze) && (
          <div className="wpv-section">
            {clientData.bodyComposition && (
              <>
                <h3 className="wpv-section-title">Composizione Corporea</h3>
                <div className="wpv-body-comp">
                  <div className="wpv-body-comp-grid">
                    <div className="wpv-body-comp-item">
                      <span className="wpv-body-comp-label">% Grasso</span>
                      <span className="wpv-body-comp-value wpv-body-comp-highlight">{clientData.bodyComposition.percentuale_grasso}%</span>
                    </div>
                    <div className="wpv-body-comp-item">
                      <span className="wpv-body-comp-label">Massa Grassa</span>
                      <span className="wpv-body-comp-value">{clientData.bodyComposition.massa_grassa_kg} kg</span>
                    </div>
                    <div className="wpv-body-comp-item">
                      <span className="wpv-body-comp-label">Massa Magra</span>
                      <span className="wpv-body-comp-value">{clientData.bodyComposition.massa_magra_kg} kg</span>
                    </div>
                    {clientData.peso && (
                      <div className="wpv-body-comp-item">
                        <span className="wpv-body-comp-label">Peso</span>
                        <span className="wpv-body-comp-value">{clientData.peso} kg</span>
                      </div>
                    )}
                  </div>
                  {clientData.measurementDate && (
                    <div className="wpv-body-comp-date">
                      Rilevazione del {new Date(clientData.measurementDate).toLocaleDateString('it-IT')}
                    </div>
                  )}
                </div>
              </>
            )}

            {clientData.pliche && Object.values(clientData.pliche).some(v => v !== null) && (
              <>
                <h3 className="wpv-section-title" style={{ marginTop: clientData.bodyComposition ? '20px' : 0 }}>Plicometria</h3>
                <div className="wpv-circ-grid">
                  {[
                    { key: 'pettorale', label: 'Pettorale' },
                    { key: 'tricipite', label: 'Tricipite' },
                    { key: 'ascellare', label: 'Ascellare' },
                    { key: 'scapolare', label: 'Sottoscapolare' },
                    { key: 'soprailiaca', label: 'Soprailiaca' },
                    { key: 'addominale', label: 'Addominale' },
                    { key: 'coscia', label: 'Coscia' },
                    { key: 'bicipite', label: 'Bicipite' },
                  ].filter(item => clientData.pliche[item.key] !== null).map(item => (
                    <div key={item.key} className="wpv-circ-item">
                      <span className="wpv-circ-label">{item.label}</span>
                      <span className="wpv-circ-value">{clientData.pliche[item.key]} mm</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {clientData.circonferenze && Object.values(clientData.circonferenze).some(v => v !== null) && (
              <>
                <h3 className="wpv-section-title" style={{ marginTop: '20px' }}>Circonferenze</h3>
                <div className="wpv-circ-grid">
                  {[
                    { key: 'spalle', label: 'Spalle' },
                    { key: 'petto', label: 'Petto' },
                    { key: 'braccio', label: 'Braccio' },
                    { key: 'vita', label: 'Vita' },
                    { key: 'fianchi', label: 'Fianchi' },
                    { key: 'coscia', label: 'Coscia' },
                    { key: 'polpaccio', label: 'Polpaccio' },
                  ].filter(item => clientData.circonferenze[item.key] !== null).map(item => (
                    <div key={item.key} className="wpv-circ-item">
                      <span className="wpv-circ-label">{item.label}</span>
                      <span className="wpv-circ-value">{clientData.circonferenze[item.key]} cm</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Descrizione */}
        <div className="wpv-section">
          <h3 className="wpv-section-title">Descrizione</h3>
          {isEditing ? (
            <textarea
              className="wpv-textarea"
              value={editedPlan.descrizione || ''}
              onChange={(e) => handleFieldChange('descrizione', e.target.value)}
              rows="4"
            />
          ) : (
            <p className="wpv-description">{displayPlan.descrizione || 'Nessuna descrizione disponibile.'}</p>
          )}
        </div>

        {/* Giorni di allenamento */}
        <div className="wpv-section">
          <h3 className="wpv-section-title">Programma di Allenamento</h3>
          {displayPlan.giorni && displayPlan.giorni.length > 0 ? displayPlan.giorni.map((giorno, dayIndex) => (
            <div key={dayIndex} className="wpv-day">
              <div className="wpv-day-header">
                <h4 className="wpv-day-title">
                  {isEditing ? (
                    <input
                      type="text"
                      className="wpv-input wpv-input-title"
                      value={editedPlan.giorni?.[dayIndex]?.nome_giorno ?? giorno.nome_giorno ?? ''}
                      onChange={(e) => {
                        const newPlan = JSON.parse(JSON.stringify(editedPlan));
                        if (!newPlan.giorni) newPlan.giorni = [];
                        if (!newPlan.giorni[dayIndex]) newPlan.giorni[dayIndex] = {};
                        newPlan.giorni[dayIndex].nome_giorno = e.target.value;
                        setEditedPlan(newPlan);
                        if (onPlanChange) onPlanChange(newPlan);
                      }}
                    />
                  ) : (
                    giorno.nome_giorno || `Giorno ${giorno.giorno_numero || dayIndex + 1}`
                  )}
                </h4>
                <span className="wpv-day-number">Giorno {giorno.giorno_numero || dayIndex + 1}</span>
              </div>

              <div className="wpv-exercises">
                {giorno.esercizi && giorno.esercizi.length > 0 ? giorno.esercizi.map((esercizio, exIndex) => (
                  <React.Fragment key={exIndex}>
                    {/* Bottone di inserimento tra esercizi */}
                    {isEditing && (
                      <div 
                        className="wpv-exercise-insert-container"
                        onMouseEnter={() => setHoveredInsertIndex({ dayIndex, exerciseIndex: exIndex })}
                        onMouseLeave={() => setHoveredInsertIndex({ dayIndex: null, exerciseIndex: null })}
                      >
                        {hoveredInsertIndex.dayIndex === dayIndex && hoveredInsertIndex.exerciseIndex === exIndex && (
                          <button
                            className="wpv-exercise-insert-btn"
                            onClick={() => handleAddExercise(dayIndex, exIndex)}
                            title="Aggiungi esercizio qui"
                            aria-label="Aggiungi esercizio"
                          >
                            <MdAdd />
                            <span>Aggiungi esercizio</span>
                          </button>
                        )}
                      </div>
                    )}
                    <div className="wpv-exercise">
                    <div className="wpv-exercise-header">
                      <h5 className="wpv-exercise-name">
                        {isEditing ? (
                          <ExerciseSearchInput
                            value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.nome || esercizio.nome || ''}
                            onChange={(val) => handleExerciseFieldChange(dayIndex, exIndex, 'nome', val)}
                            exercises={exercises}
                            exerciseGroups={exerciseGroups}
                          />
                        ) : (
                          esercizio.nome
                        )}
                      </h5>
                      {(esercizio.macchinario_target || isEditing) && (
                        <span className="wpv-machine">
                          {isEditing ? (
                            <input
                              type="text"
                              className="wpv-input wpv-input-machine"
                              value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.macchinario_target || esercizio.macchinario_target || ''}
                              onChange={(e) => handleExerciseFieldChange(dayIndex, exIndex, 'macchinario_target', e.target.value)}
                            />
                          ) : (
                            esercizio.macchinario_target
                          )}
                        </span>
                      )}
                    </div>

                    {!isEditing && (() => {
                      const videoUrl = getExerciseVideoUrl(esercizio.nome);
                      return videoUrl ? (
                        <div className="wpv-exercise-video">
                          <video src={videoUrl} controls preload="metadata" />
                        </div>
                      ) : null;
                    })()}

                    <div className="wpv-exercise-details">
                      <div className="wpv-exercise-detail">
                        <span className="wpv-detail-label">Serie:</span>
                        {isEditing ? (
                          <input
                            type="number"
                            className="wpv-input wpv-input-small"
                            value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.serie ?? esercizio.serie ?? ''}
                            onChange={(e) => handleExerciseFieldChange(dayIndex, exIndex, 'serie', parseInt(e.target.value) || 0)}
                          />
                        ) : (
                          <span className="wpv-detail-value">{esercizio.serie ?? 'N/A'}</span>
                        )}
                      </div>
                      <div className="wpv-exercise-detail">
                        <span className="wpv-detail-label">Ripetizioni:</span>
                        {isEditing ? (
                          <input
                            type="text"
                            className="wpv-input wpv-input-small"
                            value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.ripetizioni ?? esercizio.ripetizioni ?? ''}
                            onChange={(e) => handleExerciseFieldChange(dayIndex, exIndex, 'ripetizioni', e.target.value)}
                          />
                        ) : (
                          <span className="wpv-detail-value">{esercizio.ripetizioni ?? 'N/A'}</span>
                        )}
                      </div>
                      <div className="wpv-exercise-detail">
                        <span className="wpv-detail-label">Recupero:</span>
                        {isEditing ? (
                          <input
                            type="text"
                            className="wpv-input wpv-input-small"
                            value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.recupero ?? esercizio.recupero ?? ''}
                            onChange={(e) => handleExerciseFieldChange(dayIndex, exIndex, 'recupero', e.target.value)}
                          />
                        ) : (
                          <span className="wpv-detail-value">{esercizio.recupero ?? 'N/A'}</span>
                        )}
                      </div>
                      {(esercizio.tecnica_intensita || isEditing) && (
                        <div className="wpv-exercise-detail wpv-exercise-detail-technique">
                          <span className="wpv-detail-label">Tecnica:</span>
                          {isEditing ? (
                            <input
                              type="text"
                              className="wpv-input wpv-input-technique"
                              value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.tecnica_intensita ?? esercizio.tecnica_intensita ?? ''}
                              onChange={(e) => handleExerciseFieldChange(dayIndex, exIndex, 'tecnica_intensita', e.target.value)}
                            />
                          ) : (
                            <span className="wpv-detail-value">{esercizio.tecnica_intensita || 'N/A'}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {(esercizio.note || isEditing) && (
                      <div className="wpv-exercise-notes">
                        <span className="wpv-notes-label">Note:</span>
                        {isEditing ? (
                          <textarea
                            className="wpv-textarea wpv-textarea-small"
                            value={editedPlan.giorni?.[dayIndex]?.esercizi?.[exIndex]?.note ?? esercizio.note ?? ''}
                            onChange={(e) => handleExerciseFieldChange(dayIndex, exIndex, 'note', e.target.value)}
                            rows="2"
                          />
                        ) : (
                          <span className="wpv-notes-text">{esercizio.note || 'Nessuna nota'}</span>
                        )}
                      </div>
                    )}
                    {isEditing && (
                      <div className="wpv-exercise-actions">
                        <div className="wpv-exercise-move-group">
                          <button
                            className="wpv-exercise-move-btn"
                            onClick={() => handleMoveExercise(dayIndex, exIndex, 'up')}
                            title="Sposta esercizio in alto"
                            disabled={exIndex === 0}
                            aria-label="Sposta su"
                          >
                            <MdArrowUpward />
                          </button>
                          <button
                            className="wpv-exercise-move-btn"
                            onClick={() => handleMoveExercise(dayIndex, exIndex, 'down')}
                            title="Sposta esercizio in basso"
                            disabled={exIndex === giorno.esercizi.length - 1}
                            aria-label="Sposta giù"
                          >
                            <MdArrowDownward />
                          </button>
                        </div>
                        <button
                          className="wpv-exercise-delete-btn"
                          onClick={() => handleRemoveExercise(dayIndex, exIndex)}
                          title="Rimuovi esercizio"
                        >
                          <MdDelete />
                          Rimuovi
                        </button>
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                )) : (
                  isEditing && (
                    <div className="wpv-no-exercises">
                      <p>Nessun esercizio in questo giorno.</p>
                    </div>
                  )
                )}
                {/* Bottone aggiungi esercizio sempre visibile */}
                {isEditing && (
                  <button
                    className="wpv-add-exercise-btn"
                    onClick={() => handleAddExercise(dayIndex)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      width: '100%', padding: '10px', marginTop: '8px',
                      background: 'rgba(235, 205, 97, 0.06)', border: '1px dashed rgba(235, 205, 97, 0.3)',
                      borderRadius: '8px', color: '#ebcd61', fontSize: '13px', fontWeight: '500',
                      cursor: 'pointer', transition: 'all 0.2s'
                    }}
                  >
                    <MdAdd />
                    Aggiungi Esercizio
                  </button>
                )}
              </div>

              {/* Sezione Esercizi Bonus */}
              {((giorno.esercizi_bonus && giorno.esercizi_bonus.length > 0) || isEditing) && (
                <div className="wpv-bonus-section" style={{ marginTop: '12px' }}>
                  {(giorno.esercizi_bonus && giorno.esercizi_bonus.length > 0) && (
                    <>
                      <h5 style={{
                        color: '#e8a838', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase',
                        letterSpacing: '1px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
                        borderTop: '1px solid rgba(232, 168, 56, 0.2)', paddingTop: '12px'
                      }}>
                        ⭐ Esercizi Bonus
                      </h5>
                      <div className="wpv-exercises">
                        {giorno.esercizi_bonus.map((bonus, bIndex) => (
                          <div key={bIndex} className="wpv-exercise" style={{ borderLeft: '3px solid #e8a838' }}>
                            <div className="wpv-exercise-header">
                              <h5 className="wpv-exercise-name">
                                {isEditing ? (
                                  <ExerciseSearchInput
                                    value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.nome || bonus.nome || ''}
                                    onChange={(val) => handleBonusFieldChange(dayIndex, bIndex, 'nome', val)}
                                    exercises={exercises}
                                    exerciseGroups={exerciseGroups}
                                  />
                                ) : (
                                  bonus.nome
                                )}
                              </h5>
                              {(bonus.macchinario_target || isEditing) && (
                                <span className="wpv-machine">
                                  {isEditing ? (
                                    <input type="text" className="wpv-input wpv-input-machine"
                                      value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.macchinario_target || bonus.macchinario_target || ''}
                                      onChange={(e) => handleBonusFieldChange(dayIndex, bIndex, 'macchinario_target', e.target.value)}
                                    />
                                  ) : bonus.macchinario_target}
                                </span>
                              )}
                            </div>

                            {!isEditing && (() => {
                              const videoUrl = getExerciseVideoUrl(bonus.nome);
                              return videoUrl ? (
                                <div className="wpv-exercise-video">
                                  <video src={videoUrl} controls preload="metadata" />
                                </div>
                              ) : null;
                            })()}

                            <div className="wpv-exercise-details">
                              <div className="wpv-exercise-detail">
                                <span className="wpv-detail-label">Serie:</span>
                                {isEditing ? (
                                  <input type="number" className="wpv-input wpv-input-small"
                                    value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.serie ?? bonus.serie ?? ''}
                                    onChange={(e) => handleBonusFieldChange(dayIndex, bIndex, 'serie', parseInt(e.target.value) || 0)}
                                  />
                                ) : <span className="wpv-detail-value">{bonus.serie ?? 'N/A'}</span>}
                              </div>
                              <div className="wpv-exercise-detail">
                                <span className="wpv-detail-label">Ripetizioni:</span>
                                {isEditing ? (
                                  <input type="text" className="wpv-input wpv-input-small"
                                    value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.ripetizioni ?? bonus.ripetizioni ?? ''}
                                    onChange={(e) => handleBonusFieldChange(dayIndex, bIndex, 'ripetizioni', e.target.value)}
                                  />
                                ) : <span className="wpv-detail-value">{bonus.ripetizioni ?? 'N/A'}</span>}
                              </div>
                              <div className="wpv-exercise-detail">
                                <span className="wpv-detail-label">Recupero:</span>
                                {isEditing ? (
                                  <input type="text" className="wpv-input wpv-input-small"
                                    value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.recupero ?? bonus.recupero ?? ''}
                                    onChange={(e) => handleBonusFieldChange(dayIndex, bIndex, 'recupero', e.target.value)}
                                  />
                                ) : <span className="wpv-detail-value">{bonus.recupero ?? 'N/A'}</span>}
                              </div>
                              {(bonus.tecnica_intensita || isEditing) && (
                                <div className="wpv-exercise-detail wpv-exercise-detail-technique">
                                  <span className="wpv-detail-label">Tecnica:</span>
                                  {isEditing ? (
                                    <input type="text" className="wpv-input wpv-input-technique"
                                      value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.tecnica_intensita ?? bonus.tecnica_intensita ?? ''}
                                      onChange={(e) => handleBonusFieldChange(dayIndex, bIndex, 'tecnica_intensita', e.target.value)}
                                    />
                                  ) : <span className="wpv-detail-value">{bonus.tecnica_intensita || 'N/A'}</span>}
                                </div>
                              )}
                            </div>
                            {(bonus.note || isEditing) && (
                              <div className="wpv-exercise-notes">
                                <span className="wpv-notes-label">Note:</span>
                                {isEditing ? (
                                  <textarea className="wpv-textarea wpv-textarea-small"
                                    value={editedPlan.giorni?.[dayIndex]?.esercizi_bonus?.[bIndex]?.note ?? bonus.note ?? ''}
                                    onChange={(e) => handleBonusFieldChange(dayIndex, bIndex, 'note', e.target.value)}
                                    rows="2"
                                  />
                                ) : <span className="wpv-notes-text">{bonus.note || 'Nessuna nota'}</span>}
                              </div>
                            )}
                            {isEditing && (
                              <div className="wpv-exercise-actions">
                                <div className="wpv-exercise-move-group">
                                  <button className="wpv-exercise-move-btn" onClick={() => handleMoveBonusExercise(dayIndex, bIndex, 'up')}
                                    disabled={bIndex === 0} aria-label="Sposta su"><MdArrowUpward /></button>
                                  <button className="wpv-exercise-move-btn" onClick={() => handleMoveBonusExercise(dayIndex, bIndex, 'down')}
                                    disabled={bIndex === giorno.esercizi_bonus.length - 1} aria-label="Sposta giù"><MdArrowDownward /></button>
                                </div>
                                <button className="wpv-exercise-delete-btn" onClick={() => handleRemoveBonusExercise(dayIndex, bIndex)}>
                                  <MdDelete /> Rimuovi
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {isEditing && (
                          <button
                            className="wpv-add-exercise-btn"
                            onClick={() => handleAddBonusExercise(dayIndex)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                              width: '100%', padding: '10px', marginTop: '8px',
                              background: 'rgba(232, 168, 56, 0.06)', border: '1px dashed rgba(232, 168, 56, 0.3)',
                              borderRadius: '8px', color: '#e8a838', fontSize: '13px', fontWeight: '500',
                              cursor: 'pointer', transition: 'all 0.2s'
                            }}
                          >
                            <MdAdd /> Aggiungi Esercizio Bonus
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {isEditing && (!giorno.esercizi_bonus || giorno.esercizi_bonus.length === 0) && (
                    <button
                      onClick={() => handleAddBonusExercise(dayIndex)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        width: '100%', padding: '10px', marginTop: '8px',
                        background: 'transparent', border: '1px dashed rgba(232, 168, 56, 0.2)',
                        borderRadius: '8px', color: 'rgba(232, 168, 56, 0.6)', fontSize: '12px', fontWeight: '500',
                        cursor: 'pointer', transition: 'all 0.2s'
                      }}
                    >
                      ⭐ Aggiungi Esercizi Bonus (opzionale)
                    </button>
                  )}
                </div>
              )}
            </div>
          )) : (
            <div className="wpv-no-data">Nessun giorno di allenamento disponibile.</div>
          )}

          {isEditing && (
            <button
              className="wpv-add-day-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                width: '100%', padding: '14px', marginTop: '16px',
                background: 'rgba(235, 205, 97, 0.08)', border: '2px dashed rgba(235, 205, 97, 0.3)',
                borderRadius: '12px', color: '#ebcd61', fontSize: '15px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.2s'
              }}
              onClick={() => {
                const newPlan = JSON.parse(JSON.stringify(editedPlan));
                if (!newPlan.giorni) newPlan.giorni = [];
                const newDayNum = newPlan.giorni.length + 1;
                newPlan.giorni.push({ giorno_numero: newDayNum, nome_giorno: `Giorno ${newDayNum}`, esercizi: [] });
                newPlan.frequenza_settimanale = newPlan.giorni.length;
                setEditedPlan(newPlan);
                if (onPlanChange) onPlanChange(newPlan);
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(235, 205, 97, 0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(235, 205, 97, 0.08)'; }}
            >
              <MdAdd size={20} />
              Aggiungi Giorno
            </button>
          )}
        </div>

        {/* Consigli trainer */}
        {displayPlan.consigli_trainer && displayPlan.consigli_trainer.length > 0 && (
          <div className="wpv-section">
            <h3 className="wpv-section-title">Consigli del Trainer</h3>
            <ul className="wpv-tips-list">
              {displayPlan.consigli_trainer.map((consiglio, index) => (
                <li key={index} className="wpv-tip-item">
                  {isEditing ? (
                    <input
                      type="text"
                      className="wpv-input"
                      value={editedPlan.consigli_trainer[index] || consiglio}
                      onChange={(e) => {
                        const newPlan = { ...editedPlan };
                        newPlan.consigli_trainer[index] = e.target.value;
                        setEditedPlan(newPlan);
                        if (onPlanChange) {
                          onPlanChange(newPlan);
                        }
                      }}
                    />
                  ) : (
                    consiglio
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkoutPlanViewer;



